import {createRef, RefObject, useEffect, useMemo, useState} from 'react';

import {AnswerValue, LeadFormBlockQuestionAnswer, SurveyFormState} from '@target-web/leadads-form-view';

import {PagePage, SurveyDataType} from 'api/types/LeadForm';
import useAnalytics from './useAnalytics';

export interface LeadFormControllerProps {
    leadForm: SurveyDataType;
    page?: PagePage['page'];
}

function useSurveyFormController({leadForm, page}: LeadFormControllerProps) {
    const {leadAnswerAnalyticEvent} = useAnalytics();

    const [formState, setFormState] = useState<SurveyFormState>({
        answers: {},
        emptyAnswersIds: [],
        answersRefs:
            page?.blocks.reduce<Record<string, RefObject<HTMLDivElement>>>((res, cur) => {
                res[cur.id] = createRef<HTMLDivElement>();
                return res;
            }, {}) || {},
    });

    //  Не добавлено в formState, так как это чисто private переменная для облегчения поиска вариантов
    //  ответов с дополнительными условиями
    const pageAnswers = useMemo(() => {
        return (
            page?.blocks.reduce<Record<string, LeadFormBlockQuestionAnswer>>((res, cur) => {
                const blockData = cur.block_data.data;
                if (
                    blockData.type !== 'one_answer' &&
                    blockData.type !== 'multiple_answers' &&
                    blockData.type !== 'select_answer'
                )
                    return res;
                blockData.answers.forEach((answer) => {
                    res[answer.id] = answer;
                });
                return res;
            }, {}) || {}
        );
    }, [page?.blocks]);

    const validate = () => {
        const emptyAnswersIds = Object.keys(formState.answers)
            .map((id) => {
                const value = formState.answers[id].value;
                const needAnswerText = value.answer_ids?.length
                    ? value.answer_ids.reduce((acc, cur) => acc || pageAnswers[cur].type === 4, false) &&
                      !value.answer_text?.trim().length
                    : false;
                const isFilled = value?.answer_ids?.length
                    ? value.answer_ids.length > 0 && !needAnswerText
                    : !!value?.answer_text?.trim() || value.answer_scale !== undefined;

                return isFilled ? undefined : id;
            })
            .filter(Boolean) as string[];

        setFormState((prevState) => ({
            ...prevState,
            emptyAnswersIds,
        }));

        return {
            isValid: !emptyAnswersIds.length,
            emptyAnswersIds,
        };
    };

    useEffect(() => {
        setFormState((prevState) => ({
            ...prevState,
            answers:
                page?.blocks.reduce((acc, item) => {
                    acc[item.id] = {
                        value: {},
                        onChange: (value: AnswerValue) => {
                            setFormState((prevState) => {
                                let emptyAnswersIds = prevState.emptyAnswersIds;
                                if (value.answer_ids?.length || !!value.answer_text) {
                                    emptyAnswersIds = emptyAnswersIds.filter((id) => id !== item.id);
                                }

                                const answ = prevState.answers[item.id];
                                if (answ) {
                                    return {
                                        ...prevState,
                                        emptyAnswersIds,
                                        answers: {
                                            ...prevState.answers,
                                            [item.id]: {
                                                ...answ,
                                                value,
                                            },
                                        },
                                    };
                                }
                                return prevState;
                            });
                        },
                        onAnalyticEvent: () => leadAnswerAnalyticEvent({block_id: item.id}),
                    };

                    return acc;
                }, {} as SurveyFormState['answers']) || {},
            emptyAnswersIds: [],
            answersRefs: formState.answersRefs,
        }));
    }, [formState.answersRefs, leadForm, leadAnswerAnalyticEvent, page?.blocks]);

    return {formState, validate};
}

export default useSurveyFormController;
